import type { ReactNode } from 'react';
import { T, useGT } from 'gt-react';
import {
  getValueAtPath,
  parseTaskColumnPath,
  type ColumnConfigEntry,
  type SystemEntity,
  type TaskColumnPath,
} from '@erp/shared-types';
import type { TaskResponse, TaskTypeResponse } from '@erp/rpc';
import { DataTableColumnHeader } from 'src/components/common/data-table/data-table-column-header';
import { Checkbox } from 'src/components/ui/checkbox';
import {
  StatusPill,
  PriorityDot,
} from 'src/pages/authenticated/inbox/components/helpers';
import {
  formatConfiguredTaskQueueValue,
  formatTaskPathValue,
} from './taskQueueCellFormat';
import { columnDisplayFormat } from './columns/taskColumnOptions';
import { formatSnoozedUntil, isActivelySnoozed } from './taskSnooze';
import { renderAnchorSummaryCell } from './anchorSummary/AnchorSummaryCell';
import { TaskQueueQuickActions } from './TaskQueueQuickActions';
import { TaskRowMenuButton } from './TaskRowMenu';
import { SystemEntityInline } from 'src/components/system-entities';
import { bindTaskRowMenuHandlers } from './taskRowMenuHandlers';
import type { ColumnDef } from '@/lib/tanstack-table';
import type { TaskDetailSearch } from './taskDetailNav';

export type TaskQueueCellLookup = {
  /** Anchor column apiField → the record type its id references. */
  anchorReferences?: ReadonlyMap<string, SystemEntity>;
  typeNameById: Map<string, string>;
  typeById: Map<string, TaskTypeResponse>;
  agentNameById: Map<string, string>;
  userNameById: Map<string, string>;
  gt: ReturnType<typeof useGT>;
  /** Queue filters to carry onto `/tasks/$taskId` so detail can walk the list. */
  detailSearch?: TaskDetailSearch;
  onSnooze?: (task: TaskResponse) => void;
  onDelete?: (task: TaskResponse) => void;
};

function formatDate(value: Date | string | null | undefined): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString();
}

function isTaskField(
  task: TaskResponse,
  apiField: string,
): apiField is keyof TaskResponse {
  return Object.hasOwn(task, apiField);
}

function hasOwnKey(value: unknown, key: string | undefined): boolean {
  return (
    key !== undefined &&
    typeof value === 'object' &&
    value !== null &&
    Object.prototype.hasOwnProperty.call(value, key)
  );
}

/**
 * Value at a source-prefixed path. `correction` follows the playbook's
 * per-key rule: a top-level key missing from the correction falls through to
 * `input` (an explicit null does not). Anchor values arrive pre-resolved from
 * `tasks.list` keyed by their dotted path.
 */
function readTaskPath(task: TaskResponse, path: TaskColumnPath): unknown {
  const { source, segments } = path;
  switch (source) {
    case 'input':
      return getValueAtPath(task.input, segments);
    case 'correction':
      return hasOwnKey(task.correction, segments[0])
        ? getValueAtPath(task.correction, segments)
        : getValueAtPath(task.input, segments);
    case 'outcomeData':
      return getValueAtPath(task.outcomeData, segments);
    case 'metadata':
      return getValueAtPath(task.metadata, segments);
    case 'anchor':
      return task.anchor?.[segments.join('.')];
  }
}

/**
 * Read a configured apiField off the task row for custom columns. A
 * `<source>.<path>` field (see `parseTaskColumnPath`) walks that source;
 * anything else is a top-level TaskResponse key, else `metadata[apiField]`.
 */
export function readTaskField(task: TaskResponse, apiField: string): unknown {
  const path = parseTaskColumnPath(apiField);
  if (path) return readTaskPath(task, path);
  if (isTaskField(task, apiField)) {
    return task[apiField];
  }
  return task.metadata?.[apiField];
}

function renderPathValue(value: unknown, entry: ColumnConfigEntry): ReactNode {
  const { text, title } = formatTaskPathValue(
    value,
    columnDisplayFormat(entry),
  );
  if (!title) return text;
  return (
    <span title={title} className="font-mono text-[11px] text-stone-600">
      {text}
    </span>
  );
}

function renderDisplayId(task: TaskResponse): ReactNode {
  return (
    <span className="font-mono text-[11px] tabular-nums text-stone-600">
      {task.displayId ?? ''}
    </span>
  );
}

function renderTitle(
  task: TaskResponse,
  lookup: TaskQueueCellLookup,
): ReactNode {
  const agentSummary =
    task.status === 'needs_input' &&
    typeof task.metadata?.agentSummary === 'string'
      ? task.metadata.agentSummary
      : null;
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate font-medium text-stone-900">
          {task.title}
        </span>
        {isActivelySnoozed(task.snoozedUntil) && (
          <span className="shrink-0 rounded bg-amber-100 px-1.5 py-px text-[11px] font-medium text-amber-950">
            {lookup.gt('Snoozed until {when}', {
              context: 'badge on a task row that is still snoozed',
              when: formatSnoozedUntil(task.snoozedUntil ?? ''),
            })}
          </span>
        )}
      </span>
      {agentSummary && (
        <span className="line-clamp-2 text-[11px] leading-snug text-amber-800">
          {agentSummary}
        </span>
      )}
    </span>
  );
}

function renderType(
  task: TaskResponse,
  lookup: TaskQueueCellLookup,
): ReactNode {
  if (!task.taskTypeId) return null;
  return (
    <span className="rounded bg-stone-100 px-1.5 py-px text-[11px] text-stone-700">
      {lookup.typeNameById.get(task.taskTypeId) ??
        lookup.gt('Unknown type', {
          context: 'fallback label for a task type that failed to load',
        })}
    </span>
  );
}

function renderPriority(task: TaskResponse): ReactNode {
  return (
    <span className="flex items-center gap-1.5 capitalize">
      <PriorityDot priority={task.priority} />
      {task.priority}
    </span>
  );
}

function renderAssignee(
  task: TaskResponse,
  lookup: TaskQueueCellLookup,
): ReactNode {
  if (task.assigneeAgentDefinitionId) {
    return (
      lookup.agentNameById.get(task.assigneeAgentDefinitionId) ?? (
        <T context="fallback label for an assigned agent that failed to load">
          Assigned agent
        </T>
      )
    );
  }
  if (task.assigneeUserId) {
    return (
      lookup.userNameById.get(task.assigneeUserId) ?? (
        <T context="fallback label for an assigned user that failed to load">
          Assigned
        </T>
      )
    );
  }
  return (
    <span className="text-muted-foreground">
      <T context="label for a task with no assigned user or agent">
        Unassigned
      </T>
    </span>
  );
}

function renderOutcome(task: TaskResponse): ReactNode {
  if (!task.outcome) return null;
  return (
    <span className="rounded bg-stone-100 px-1.5 py-px text-[11px] capitalize text-stone-700">
      {task.outcome}
    </span>
  );
}

const BUILTIN_CELL_RENDERERS: Record<
  string,
  (task: TaskResponse, lookup: TaskQueueCellLookup) => ReactNode
> = {
  displayId: renderDisplayId,
  title: renderTitle,
  type: renderType,
  status: (task) => <StatusPill status={task.status} />,
  priority: renderPriority,
  assignee: renderAssignee,
  outcome: renderOutcome,
  anchor: renderAnchorSummaryCell,
  dueDate: (task) => formatDate(task.dueDate),
  updatedAt: (task) => formatDate(task.updatedAt),
};

export function renderTaskQueueCell({
  entry,
  task,
  lookup,
}: {
  entry: ColumnConfigEntry;
  task: TaskResponse;
  lookup: TaskQueueCellLookup;
}): ReactNode {
  const render = BUILTIN_CELL_RENDERERS[entry.id];
  if (render) return render(task, lookup);
  const referenced = lookup.anchorReferences?.get(entry.apiField);
  if (referenced) {
    const id = readTaskField(task, entry.apiField);
    return typeof id === 'string' && id ? (
      <SystemEntityInline entityType={referenced} entityId={id} />
    ) : null;
  }
  if (parseTaskColumnPath(entry.apiField)) {
    return renderPathValue(readTaskField(task, entry.apiField), entry);
  }
  return formatConfiguredTaskQueueValue(
    readTaskField(task, entry.apiField),
    columnDisplayFormat(entry),
  );
}

export const TASK_QUEUE_SELECT_COLUMN_ID = 'select';

/**
 * `useTable` meta for the queue. Columns read the lookup from the table, not
 * from a closure, so they can be built once per column shape: `flexRender`
 * mounts a function cell as a component, and new functions every render would
 * remount every cell (refetching what each one loads) on each queue poll.
 */
export type TaskQueueTableMeta = { taskQueueLookup: TaskQueueCellLookup };

/** The lookup on the queue table's meta. */
export function taskQueueLookup(table: {
  options: { meta?: unknown };
}): TaskQueueCellLookup {
  const meta = table.options.meta as Partial<TaskQueueTableMeta> | undefined;
  if (!meta?.taskQueueLookup) {
    throw new Error('Task queue columns require TaskQueueTableMeta');
  }
  return meta.taskQueueLookup;
}

/**
 * Row-selection checkbox column for bulk actions. Not in the user's column
 * config (`enableHiding: false`), so it never appears in the columns popover
 * and is pinned ahead of any reordering. Rows are stretched links, so the
 * checkbox rises above the overlay (`relative z-10`) and swallows the click.
 */
const selectColumn: ColumnDef<TaskResponse> = {
  id: TASK_QUEUE_SELECT_COLUMN_ID,
  size: 32,
  enableHiding: false,
  enableSorting: false,
  header: ({ table }) => (
    <div className="relative z-10 flex items-center">
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label={taskQueueLookup(table).gt('Select all tasks on this page', {
          context: 'table select-all rows checkbox on the tasks queue',
        })}
      />
    </div>
  ),
  cell: ({ row, table }) => (
    <div
      className="relative z-10 flex items-center"
      onClick={(event) => event.stopPropagation()}
    >
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label={taskQueueLookup(table).gt('Select task', {
          context: 'table select single row checkbox on the tasks queue',
        })}
      />
    </div>
  ),
};

/** Inline buttons for the task type's declared actions. */
const quickActionsColumn: ColumnDef<TaskResponse> = {
  id: 'quickActions',
  header: ({ table }) => (
    <span>
      {taskQueueLookup(table).gt('Actions', {
        context: 'column header for task queue quick actions',
      })}
    </span>
  ),
  enableSorting: false,
  cell: ({ row, table }) => {
    const lookup = taskQueueLookup(table);
    const task = row.original;
    const taskType = task.taskTypeId
      ? lookup.typeById.get(task.taskTypeId)
      : undefined;
    return (
      <TaskQueueQuickActions
        task={task}
        taskType={taskType}
        detailSearch={lookup.detailSearch}
      />
    );
  },
};

// Secondary row actions sit in a narrow trailing column, right-aligned at the
// end of the row, so the `⋮` never wraps under the quick actions.
const rowMenuColumn: ColumnDef<TaskResponse> = {
  id: 'rowMenu',
  header: () => null,
  enableSorting: false,
  size: 40,
  meta: { align: 'right' },
  cell: ({ row, table }) => {
    const task = row.original;
    return (
      <TaskRowMenuButton
        task={task}
        {...bindTaskRowMenuHandlers(task, taskQueueLookup(table))}
      />
    );
  },
};

/**
 * Column defs for the configured columns. They read the lookup from the
 * table's {@link TaskQueueTableMeta}, so they depend only on the column shape.
 */
export function buildTaskQueueColumns({
  configuredColumns,
  sortableColumnIds,
  selectable = false,
  showQuickActions = true,
}: {
  configuredColumns: ColumnConfigEntry[];
  sortableColumnIds: ReadonlySet<string>;
  /** Prepend the row-selection checkbox column (bulk actions). */
  selectable?: boolean;
  /** Include the quick-actions column (off when no row can have actions). */
  showQuickActions?: boolean;
}): ColumnDef<TaskResponse>[] {
  const columns: ColumnDef<TaskResponse>[] = selectable ? [selectColumn] : [];
  for (const entry of configuredColumns) {
    if (entry.hidden) continue;
    // Path columns (input/outcome/anchor JSON) have no backend sort.
    const sortable =
      sortableColumnIds.has(entry.id) && !parseTaskColumnPath(entry.apiField);
    columns.push({
      id: entry.id,
      accessorFn: (row: TaskResponse) => readTaskField(row, entry.apiField),
      header: ({ column }) =>
        sortable ? (
          <DataTableColumnHeader column={column} title={entry.header} />
        ) : (
          <span>{entry.header}</span>
        ),
      enableSorting: sortable,
      cell: ({ row, table }) =>
        renderTaskQueueCell({
          entry,
          task: row.original,
          lookup: taskQueueLookup(table),
        }),
    });
  }
  if (showQuickActions) columns.push(quickActionsColumn);
  columns.push(rowMenuColumn);
  return columns;
}
